import React, { useState, useEffect } from 'react';
import { categoryAPI } from '../../services/api.js';
import toast from 'react-hot-toast';
import CategoryForm from '../../components/admin/CategoryForm.jsx';

const CategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories with hierarchy
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryAPI.getHierarchy();
      console.log('Categories API response:', response);
      
      // Handle different API response structures
      let categoryList = [];
      
      if (Array.isArray(response)) {
        categoryList = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        categoryList = response.data;
      } else if (response && response.categories && Array.isArray(response.categories)) {
        categoryList = response.categories;
      } else if (response && response.result && Array.isArray(response.result)) {
        categoryList = response.result;
      } else {
        if (response && typeof response === 'object') {
          categoryList = Object.values(response);
        } else {
          categoryList = [];
        }
      }
      
      console.log('Processed categories:', categoryList);
      setCategories(categoryList);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        toast.error('Delete endpoint not implemented yet');
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category');
      }
    }
  };

  // Edit category
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  // Add new category
  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Safe render function
  const renderCategoryTree = (categoryList, level = 0) => {
    if (!Array.isArray(categoryList)) {
      console.error('categoryList is not an array:', categoryList);
      return (
        <div style={{ 
          padding: 'var(--space-4)', 
          color: 'var(--error)',
          textAlign: 'center'
        }}>
          Error: Categories data is not in correct format
        </div>
      );
    }

    if (categoryList.length === 0) {
      return (
        <div style={{ 
          padding: 'var(--space-4)', 
          textAlign: 'center',
          color: 'var(--text-light)',
          fontStyle: 'italic'
        }}>
          No categories found at this level
        </div>
      );
    }

    return categoryList.map((category) => {
      if (!category || typeof category !== 'object') {
        return null;
      }

      const categoryId = category._id || category.id || Math.random().toString();
      const categoryName = category.name || 'Unnamed Category';
      const categoryDescription = category.description || '';
      const categoryStatus = category.status || 'inactive';
      const children = category.children || [];
      const categoryLevel = category.level || level;

      // Calculate padding based on screen size
      const mobilePaddingLeft = isSmallScreen ? level * 12 : level * 20;
      const desktopPaddingLeft = level * 24;

      return (
        <div key={categoryId} style={{ 
          marginLeft: isMobile ? `${mobilePaddingLeft}px` : `${desktopPaddingLeft}px`,
          transition: 'margin-left 0.3s ease'
        }}>
          {/* Category Item */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: isSmallScreen ? '8px 12px' : (isMobile ? '12px 16px' : 'var(--space-3) var(--space-4)'),
            borderBottom: '1px solid var(--border-light)',
            backgroundColor: level === 0 ? 'var(--bg-light)' : 'transparent',
            transition: 'background-color 0.2s ease',
            flexWrap: isSmallScreen ? 'wrap' : 'nowrap',
            gap: isSmallScreen ? '8px' : 'var(--space-2)'
          }}>
            {/* Expand/Collapse Button */}
            {children && children.length > 0 && (
              <button
                onClick={() => toggleCategory(categoryId)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: isSmallScreen ? '4px' : 'var(--space-2)',
                  marginRight: isSmallScreen ? '4px' : 'var(--space-2)',
                  fontSize: isSmallScreen ? '16px' : 'var(--font-size-lg)',
                  color: 'var(--text-light)',
                  flexShrink: 0,
                  minWidth: isSmallScreen ? '24px' : '32px'
                }}
                aria-label={expandedCategories.has(categoryId) ? 'Collapse category' : 'Expand category'}
              >
                {expandedCategories.has(categoryId) ? '📂' : '📁'}
              </button>
            )}
            
            {/* Category Icon based on level */}
            <div style={{ 
              marginRight: isSmallScreen ? '8px' : 'var(--space-3)',
              fontSize: isSmallScreen ? '14px' : '16px',
              flexShrink: 0
            }}>
              {level === 0 ? '📁' : level === 1 ? '📂' : '📄'}
            </div>

            {/* Category Info */}
            <div style={{ 
              flex: 1,
              minWidth: 0, // Prevent overflow
              overflow: 'hidden'
            }}>
              <div style={{ 
                fontWeight: '600', 
                color: 'var(--text-dark)',
                marginBottom: isSmallScreen ? '2px' : 'var(--space-1)',
                fontSize: isSmallScreen ? '14px' : (isMobile ? '15px' : 'var(--font-size-base)'),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {categoryName}
                {categoryLevel > 0 && !isSmallScreen && (
                  <span style={{
                    marginLeft: 'var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-light)',
                    background: 'var(--bg-gray)',
                    padding: 'var(--space-1) var(--space-2)',
                    borderRadius: 'var(--radius-sm)'
                  }}>
                    Level {categoryLevel}
                  </span>
                )}
              </div>
              
              {categoryDescription && !isSmallScreen && (
                <div style={{ 
                  fontSize: isMobile ? '13px' : 'var(--font-size-sm)',
                  color: 'var(--text-light)',
                  marginBottom: 'var(--space-1)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {categoryDescription}
                </div>
              )}

              {!isMobile && (
                <div style={{ 
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-light)'
                }}>
                  Slug: {category.seo?.slug || category.slug || 'no-slug'}
                  {category.parent && category.parent.name && (
                    <span style={{ marginLeft: 'var(--space-3)' }}>
                      Parent: {category.parent.name}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Status Badge - Only show on medium+ screens */}
            {!isSmallScreen && (
              <div style={{ 
                marginRight: isMobile ? '12px' : 'var(--space-4)',
                flexShrink: 0 
              }}>
                <span 
                  style={{ 
                    backgroundColor: categoryStatus === 'active' ? 'var(--success)' : 'var(--error)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: isMobile ? '11px' : 'var(--font-size-xs)',
                    fontWeight: '600',
                    display: 'inline-block',
                    textTransform: 'uppercase'
                  }}
                >
                  {categoryStatus}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: isSmallScreen ? '4px' : 'var(--space-2)',
              flexShrink: 0
            }}>
              <button 
                className="btn btn-secondary"
                onClick={() => handleEditCategory(category)}
                style={{ 
                  padding: isSmallScreen ? '4px 8px' : (isMobile ? '6px 12px' : 'var(--space-2) var(--space-3)'),
                  fontSize: isSmallScreen ? '12px' : (isMobile ? '13px' : 'var(--font-size-sm)'),
                  minHeight: isSmallScreen ? '28px' : '32px'
                }}
                aria-label="Edit category"
              >
                {isSmallScreen ? '✏️' : 'Edit'}
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleDeleteCategory(categoryId)}
                style={{ 
                  padding: isSmallScreen ? '4px 8px' : (isMobile ? '6px 12px' : 'var(--space-2) var(--space-3)'),
                  fontSize: isSmallScreen ? '12px' : (isMobile ? '13px' : 'var(--font-size-sm)'),
                  minHeight: isSmallScreen ? '28px' : '32px'
                }}
                aria-label="Delete category"
              >
                {isSmallScreen ? '🗑️' : 'Delete'}
              </button>
            </div>
          </div>

          {/* Children */}
          {children && children.length > 0 && expandedCategories.has(categoryId) && (
            <div style={{ 
              borderLeft: level > 0 ? '2px solid var(--border-light)' : 'none',
              marginLeft: level > 0 ? (isSmallScreen ? '6px' : '12px') : '0'
            }}>
              {renderCategoryTree(children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Mobile header styles
  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: isSmallScreen ? 'flex-start' : 'center',
    marginBottom: isSmallScreen ? 'var(--space-4)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
    flexDirection: isSmallScreen ? 'column' : 'row',
    gap: isSmallScreen ? 'var(--space-3)' : '0'
  };

  const titleStyle = {
    fontSize: isSmallScreen ? '20px' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
    color: 'var(--text-dark)',
    marginBottom: isSmallScreen ? '4px' : 'var(--space-2)',
    lineHeight: 1.2
  };

  const addButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: isSmallScreen ? '8px 12px' : (isMobile ? '10px 16px' : 'var(--space-3) var(--space-5)'),
    fontSize: isSmallScreen ? '14px' : (isMobile ? '15px' : 'var(--font-size-base)'),
    minHeight: isSmallScreen ? '36px' : '44px',
    alignSelf: isSmallScreen ? 'stretch' : 'center',
    width: isSmallScreen ? '100%' : 'auto'
  };

  return (
    <div style={{ 
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ 
          flex: 1,
          minWidth: 0 // Prevent overflow
        }}>
          <h1 style={titleStyle}>
            Categories <span style={{ color: 'var(--accent-gold)' }}>Management</span>
          </h1>
          <p style={{ 
            color: 'var(--text-secondary)',
            fontSize: isSmallScreen ? '13px' : (isMobile ? '14px' : 'var(--font-size-base)'),
            marginBottom: isSmallScreen ? 'var(--space-2)' : 0
          }}>
            {isSmallScreen ? 'Manage category hierarchy' : 'Manage your category hierarchy and organization'}
          </p>
        </div>
        <button 
          className="btn btn-gold"
          onClick={handleAddCategory}
          style={addButtonStyle}
        >
          <span style={{ fontSize: isSmallScreen ? '16px' : '18px' }}>+</span>
          {isSmallScreen ? 'Add Category' : 'Add New Category'}
        </button>
      </div>

      {/* Categories Tree */}
      <div className="card" style={{
        overflow: 'hidden',
        marginBottom: isSmallScreen ? 'var(--space-4)' : 'var(--space-6)'
      }}>
        <div className="card-header" style={{
          padding: isSmallScreen ? '12px 16px' : (isMobile ? '16px' : 'var(--space-4)')
        }}>
          <h3 style={{ 
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: isSmallScreen ? '8px' : 'var(--space-2)',
            fontSize: isSmallScreen ? '16px' : (isMobile ? '17px' : 'var(--font-size-lg)'),
            overflow: 'hidden'
          }}>
            <span style={{ color: 'var(--accent-gold)' }}>📁</span>
            <span style={{ 
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              Category Hierarchy ({Array.isArray(categories) ? categories.length : 0} total)
            </span>
          </h3>
        </div>
        
        <div className="card-body" style={{ 
          padding: 0,
          maxWidth: '100%',
          overflowX: 'auto'
        }}>
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-6)' : 'var(--space-8)',
              color: 'var(--text-light)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
            }}>
              <div style={{
                width: isSmallScreen ? '32px' : '40px',
                height: isSmallScreen ? '32px' : '40px',
                border: '3px solid var(--border-light)',
                borderTopColor: 'var(--accent-gold)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ 
                fontSize: isSmallScreen ? '14px' : '16px'
              }}>
                Loading categories...
              </p>
            </div>
          ) : !Array.isArray(categories) || categories.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-6)' : 'var(--space-8)',
              color: 'var(--text-light)'
            }}>
              <div style={{ 
                fontSize: isSmallScreen ? '48px' : '64px',
                marginBottom: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)',
                opacity: 0.5
              }}>
                📁
              </div>
              <p style={{ 
                fontSize: isSmallScreen ? '16px' : '18px',
                marginBottom: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)'
              }}>
                No categories found.
              </p>
              <button 
                className="btn btn-gold"
                onClick={handleAddCategory}
                style={{ 
                  padding: isSmallScreen ? '10px 20px' : 'var(--space-3) var(--space-5)',
                  fontSize: isSmallScreen ? '14px' : '16px'
                }}
              >
                Add Your First Category
              </button>
            </div>
          ) : (
            <div className="table-container" style={{ 
              minWidth: isSmallScreen ? '300px' : (isMobile ? '400px' : 'auto'),
              overflowX: 'auto'
            }}>
              {/* Header Row - Only show on larger screens */}
              {!isMobile && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: isSmallScreen ? '12px 16px' : (isMobile ? '16px' : 'var(--space-4)'),
                  borderBottom: '2px solid var(--border-light)',
                  backgroundColor: 'var(--bg-light)',
                  fontWeight: '600',
                  color: 'var(--text-dark)',
                  fontSize: isSmallScreen ? '13px' : 'var(--font-size-sm)',
                  position: 'sticky',
                  left: 0,
                  minWidth: isSmallScreen ? '300px' : (isMobile ? '400px' : 'auto')
                }}>
                  <div style={{ 
                    width: isSmallScreen ? '40px' : '60px',
                    flexShrink: 0 
                  }}></div>
                  <div style={{ 
                    flex: 1,
                    flexShrink: 0,
                    minWidth: '150px'
                  }}>Category Name</div>
                  <div style={{ 
                    width: isSmallScreen ? '80px' : '100px', 
                    textAlign: 'center',
                    flexShrink: 0
                  }}>Status</div>
                  <div style={{ 
                    width: isSmallScreen ? '120px' : '150px', 
                    textAlign: 'center',
                    flexShrink: 0
                  }}>Actions</div>
                </div>
              )}

              {/* Category Tree */}
              {renderCategoryTree(categories)}
            </div>
          )}
        </div>
      </div>

      {/* Status Summary - Mobile Only */}
      {isMobile && categories.length > 0 && (
        <div className="card" style={{
          marginTop: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)',
          padding: isSmallScreen ? '12px 16px' : 'var(--space-4)',
          fontSize: isSmallScreen ? '13px' : '14px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: isSmallScreen ? '8px' : 'var(--space-2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: 'var(--success)'
              }}></div>
              <span>Active: {categories.filter(c => c.status === 'active').length}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: 'var(--error)'
              }}></div>
              <span>Inactive: {categories.filter(c => c.status !== 'active').length}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ color: 'var(--accent-gold)' }}>📁</span>
              <span>Total: {categories.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showForm && (
        <CategoryForm 
          category={editingCategory}
          onClose={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingCategory(null);
            fetchCategories();
          }}
        />
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .card {
          background-color: var(--bg-white);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border-light);
          overflow: hidden;
          transition: all 0.2s ease;
        }
        
        .card:hover {
          box-shadow: var(--shadow-md);
        }
        
        .card-header {
          border-bottom: 1px solid var(--border-light);
          background-color: var(--bg-light);
        }
        
        .btn {
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
        }
        
        .btn-gold {
          background-color: var(--accent-gold);
          color: white;
        }
        
        .btn-gold:hover {
          background-color: var(--accent-gold-dark);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }
        
        .btn-secondary {
          background-color: var(--bg-gray);
          color: var(--text-dark);
          border: 1px solid var(--border-medium);
        }
        
        .btn-secondary:hover {
          background-color: var(--border-light);
          border-color: var(--border-dark);
        }
        
        .btn-danger {
          background-color: var(--error);
          color: white;
        }
        
        .btn-danger:hover {
          background-color: var(--error-dark);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        /* Touch-friendly styles for mobile */
        @media (max-width: 768px) {
          .btn-gold:active,
          .btn-secondary:active,
          .btn-danger:active {
            transform: scale(0.98);
            transition: transform 0.1s ease;
          }
        }

        /* Extra small phones */
        @media (max-width: 375px) {
          .card {
            border-radius: var(--radius-md);
          }
          
          .btn-gold {
            font-weight: 600;
          }
        }

        /* Tablet adjustments */
        @media (min-width: 769px) and (max-width: 1024px) {
          .card {
            border-radius: var(--radius-lg);
          }
        }
      `}</style>
    </div>
  );
};

export default CategoriesManagement;